import React, { useCallback, useEffect, useRef, useState} from "react";
import { projData } from "../../globals/data";
import { AnimatePresence, motion, transform, usePresence } from "framer-motion";
import TsThumbComponent from "../../components/tsThumbComponent/TsThumbComponent";
import './styles.css';
import debounce from 'lodash.debounce';
import NewColourMode from "../../components/NewColourMode/NewColourMode";
import ColourMode from "../../components/ColourMode/ColourMode";
import { useNavigate } from "react-router-dom";
import CornerHover from "../../components/CornerHover/CornerHover";
import { itemFLVariants, mainHeroVariants, subHeroVariants, tsScrollLineWrapperVariants } from "./variants";
import { HomeTsProjTotalTransLength } from "../../components/HomepageOverlayTransition/transitionVariables";
import { smoothScrollTo } from "../../globals/helpers";
import { extVideoEndPoint, extVideoInStart, videoShrinkDuration } from "../loader/variables";


const HomepageTs = ({setTriggerHomepageToProjectTransition, isFirstLoad, setHomeToArchiveTransitionTrigger, isFromArchive}) => {
    const navigate = useNavigate();
    const itemWrapperRef = useRef(null);
    const mHeroRef = useRef(null);
    const [isPresent, safeToRemove] = usePresence();
    const [defaultExit, setDefaultExit] = useState(false);
    const [selected, setSelected] = useState(false);
    const [exitTrigger, setExitTrigger] = useState(false);
    const [isEnterLoad, setIsEnterLoad] = useState(true);
    const [firstLoadScalers, setFirstLoadScalers] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeMetaIndex, setActiveMetaIndex] = useState(0);
    const [scrollDirection, setScrollDirection] = useState(null);
    const [useMobileVideo, setUseMobileVideo] = useState(null);
    const itemRefs = useRef([]);
    const revSclrRefs = useRef([]);
    const heroSpanRef = useRef(null);
    const hollowRefs = useRef([]);
    const scrollLineRefs = useRef([]);
    const scrollLineWrapperRefs = useRef(null);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isFilmMode, setIsFilmMode] = useState(true);
    const [activeGoTo, setActiveGoTo] = useState(true);
    const [isColourMode, setIsColourMode] = useState(true);
    const [uiTrim, setUiTrim] = useState(null);

    const testVariants = {
        initial: {
            opacity: 1
        }, animate: {
            opacity: 1
        }, exit: {
            opacity: 0,
            transition: {
                duration: 0.65
            }
        }
    }

    const fmVariants = {
        initial: {
            transform: "translate3d(0, 100%, 0)"
        }, animate: (i) => ({
            transform: "translate3d(0, 0%, 0)",
            transition: {
                duration: i ? 1.5 : 1,
                delay: i ? extVideoEndPoint - 0.5: 0,
                ease: [0.16, 1, 0.3, 1]
            }
        }), exit: {
            transform: "translate3d(0, -100%, 0)",
            transition: {
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1]
            }
        }
    }

    const metaItemVariants = {
        initial:{
            transform: "translate3d(0, 100%, 0)"
        }, animate: (i) => ({
            transform: "translate3d(0, 0, 0)",
            transition: {
                duration: i[1] ? 1.5 : 1,
                delay: i[1] ? extVideoEndPoint - 0.5 + i[0] : i[0],
                ease: [0.16, 1, 0.3, 1]
            }
        }), exit: (i) => ({
            transform: "translate3d(0, -100%, 0)",
            transition: {
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
                delay: i[0]
            }
        })
    }

    const tsItemVariants = {
        exit: (i) => ({
            opacity: 0,
            pointerEvents: "none",
            //1 isSelected 2 selected 4 archive
            transition: {
                duration:   !i[2] ? 
                                i[4] ? 
                                    0.35 
                                : 0.75 
                            : i[1] ? 0.1 : 0.75,
                delay: ((i[1] && i[2]) || (i[4] && i[1])) ? 
                        1.2 
                    : i[4] ? 
                        0 : 
                            i[0] * 0.025,
                ease: !i[2] ? [0.55, 0, 1, 0.45] : [0.85, 0, 0.15, 1],
                pointerEvents: {
                    delay: i[2] ? 9*HomeTsProjTotalTransLength/10 : 0.75,
                    duration: 0
                }
            }
        })
    }

    function easeOutExpo(x: number): number {
        return x === 1 ? 1 : 1 - Math.pow(2, -10 * x);
        }

    function easeInOutQuint(x: number): number {
        return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    }

    const debouncedGoTo = useCallback(
        debounce(() => {
            setActiveGoTo(true);
        }, 100)
    ,[]);

    const debouncedActiveIndex = useCallback(
        debounce((idx) => {
            setActiveIndex(idx);
            jsActive=idx;
        }, 25)
    ,[]);

    function scrollLineClick(idx){
        let targOffset = (window.innerHeight/4) * idx;
        itemWrapperRef.current.style.scrollSnapType = "none"
        smoothScrollTo(
            itemWrapperRef.current,
            0,
            targOffset,
            1000,
            [0.16, 1, 0.99, 1]
        );
        setTimeout(() => {
            itemWrapperRef.current.style.scrollSnapType = null;
        }, 1000)
    }

    const toggleFilmMode = (e) => {
        setIsFilmMode(!isFilmMode)
    }
      
    const scrollToYOffset = (ref, targetOffset, duration = 1000) => {
        if (!ref) return;
      
        const start = ref.scrollTop; // Starting scroll position
        const startTime = performance.now(); // Start time
      
      
        const easingFunction = (t) => easeOutExpo(t);
      
        const animateScroll = (currentTime) => {
          const elapsedTime = currentTime - startTime;
          const progress = Math.min(elapsedTime / duration, 1); // Clamp progress to [0, 1]
          const easedProgress = easingFunction(progress); // Apply easing function
          const currentOffset = start + (targetOffset - start) * easedProgress;
      
          ref.scrollTop = currentOffset; // Update scroll position
      
          if (progress < 1) {
            requestAnimationFrame(animateScroll); // Continue animation
          }
        };
      
        requestAnimationFrame(animateScroll); // Start the animation loop
    };

    const handleItemClick = (idx) => {
        itemWrapperRef.current.style.scrollSnapType = "none";
        if((idx) !== activeIndex){
            let scrollOffset = ((idx)*(window.innerHeight/4))
            let scrollIndex = 1 + Math.floor(scrollOffset / (window.innerHeight/4));
            setActiveMetaIndex(scrollIndex-1)
            scrollToYOffset(itemWrapperRef.current, scrollOffset);
            //need to debounce this everytime a user clicks
            setTimeout(() => {
                itemWrapperRef.current.style.scrollSnapType = null
            }, 1000)
        }else{
            try{itemWrapperRef.current.style.overflow = "hidden"}catch{}
            setSelected(true);
            setTriggerHomepageToProjectTransition([idx, 0]);
            let targSlug = projData[activeIndex].title;
            setTimeout(() => {
                navigate('/projects/'+targSlug, {state:{isFromHome:true}});
            }, 100)
        }
    }

    useEffect(() => {
        if(!isPresent){
            let targUrl = window.location.href;
            if(!targUrl.includes("projects")){
                if(targUrl.includes("archive")){
                    setHomeToArchiveTransitionTrigger([true, true]);
                }
                setDefaultExit(true);
            } 
            safeToRemove(true);
        }
    }, [isPresent])

    useEffect(() => {
        try{
            if(revSclrRefs.current){
                revSclrRefs.current[0].style.transform = "scale(1, 1.25)";
                itemRefs.current[1].style.transform = "translate3d(-50%, 0px, 0px)  scale(1.125, 1)";
                revSclrRefs.current[1].style.transform = "scale(1, 1.125)";
            }
        }catch{}
    }, [revSclrRefs])

    let jsActive = 0;


    
    useEffect(() => {
        let lastScrollTop = 0; // Tracks the previous vertical scroll position

        const handleScrollEnd = (event) => {
            debouncedGoTo();
        }

        const handleScroll = (event) => {
        
            setHasScrolled(true);
            const target = event.target;
            setActiveGoTo(false);
            debouncedGoTo.cancel();
            // Determine vertical scroll direction
            const currentScrollTop = target.scrollTop;
            const verticalDirection = currentScrollTop > lastScrollTop ? "down" : currentScrollTop < lastScrollTop ? "up" : "none";
            let scrollDelta = Math.abs(currentScrollTop - lastScrollTop);

            if(currentScrollTop < (window.innerHeight/4)){
                let heroProg = currentScrollTop/(window.innerHeight/4);

                let deltaVal = 10;
                heroSpanRef.current.style.transform = `translate3d(0%, ${-2 * (heroProg * (window.innerHeight/4))}px, 0)`
                heroSpanRef.current.style.animation = 'unset';
                heroSpanRef.current.style.opacity = 1 - (1 * heroProg);
                for(let i=0; i< hollowRefs.current.length; i++){
                    let targRef = hollowRefs.current[i];
                    targRef.style.transform = `translate3d(0%, ${(deltaVal * heroProg * (i+1)) - 2 * (heroProg * (window.innerHeight/4))}px, 0)`
                    targRef.style.animation = 'unset';
                    targRef.style.opacity = '1';
                }
            }

            if(verticalDirection != scrollDirection) setScrollDirection(verticalDirection)
            
            // let offset = (window.innerHeight/8)
            let activeDivision = 0.5 + (currentScrollTop)/((window.innerHeight/4));
            if(Math.floor(activeDivision) !== jsActive){
                try{
                    itemRefs.current[jsActive].classList.remove("is-active")
                } catch{}
            }
            try{
                let activeTarg = Math.floor(activeDivision);
                let priorSibling = Math.max(0, activeTarg-1)
                let postSibling = Math.min(scrollLineRefs.current.length-1, activeTarg+1)

                scrollLineRefs.current[activeTarg].style.transform = "scaleX(1.75)";
                scrollLineRefs.current[activeTarg].style.background = "var(--light-color)";
                if(activeTarg > 0){
                    scrollLineRefs.current[priorSibling].style.transform = "scaleX(1.25)";
                    scrollLineRefs.current[priorSibling].style.background = "var(--unselected-color)";
                }
                if(postSibling !== activeTarg){
                    scrollLineRefs.current[postSibling].style.transform = "scaleX(1.25)";
                    scrollLineRefs.current[postSibling].style.background = "var(--unselected-color)";
                }
                for(let i=0; i<scrollLineRefs.current.length; i++){
                    if(i < priorSibling || i > postSibling){
                        scrollLineRefs.current[i].style.transform = "scaleX(1)";
                        scrollLineRefs.current[i].style.background = "var(--grid-lines)";
                    }
                }
            } catch{}
            // debouncedActiveIndex.cancel()
            debouncedActiveIndex(Math.floor(activeDivision));
            let scrollActiveIndex = Math.round(activeDivision);
            let startOffset = 0;


            let scrollOffsets = []
            for(let i=0; i<itemRefs.current.length; i++){
                scrollOffsets.push(startOffset + (i*0.25))
            }            

            let relWindowProg = currentScrollTop/window.innerHeight;  
            let progress = (activeDivision % 1);
            let relativePosition = activeDivision - 0.5;
            let relativeProgress = relativePosition % 1;

            let test = scrollOffsets.map(offset => offset - relWindowProg);

            for(let i=0; i<itemRefs.current.length; i++){
                let windowVal = test[i];
                if(windowVal <= 0.5 && windowVal >= 0){
                    let currentProgress = easeInOutQuint(1 - (windowVal * 2));
                    itemRefs.current[i].style.transform = `translate3d(-50%, 0, 0) scale(${1 + (0.25 * currentProgress)}, 1)`;
                    revSclrRefs.current[i].style.transform = `scale(1, ${1 + (0.25 * currentProgress)})`;
                    itemRefs.current[i].style.opacity = `${0.35 + (0.65 * currentProgress)}`
                    // itemRefs.current[i+2].style.filter = `blur(${scrollDelta * 0.1}px)`
                } else if(windowVal >= -0.5 && windowVal < 0){
                    let currentProgress = easeInOutQuint(1 - Math.abs(windowVal * 2));
                    itemRefs.current[i].style.transform = `translate3d(-50%, 0, 0) scale(${1 + (0.25 * currentProgress)}, 1)`
                    revSclrRefs.current[i].style.transform = `scale(1, ${1 + (0.25 * currentProgress)})`;
                    itemRefs.current[i].style.opacity = `${0.35 + (0.65 * currentProgress)}`;
                    // itemRefs.current[i+2].style.filter = `blur(${scrollDelta * 0.1}px)`
                }
            }
            // Update last scroll position
            lastScrollTop = currentScrollTop;
        };

        
        if(itemWrapperRef.current){
            try{
                itemWrapperRef.current.addEventListener("scroll", handleScroll);
                itemWrapperRef.current.addEventListener("scrollend", handleScrollEnd);
            }catch{}
        }
    }, [itemWrapperRef])

    useEffect(() => {
        setActiveMetaIndex(activeIndex);
        try{
            if(isEnterLoad){
                if(isFirstLoad){
                    setTimeout(() => {
                        itemRefs.current[activeIndex].classList.add("is-active");    
                    }, extVideoEndPoint*1000)
                }else{
                    setTimeout(() => {
                        itemRefs.current[activeIndex].classList.add("is-active");
                    }, 500)
                }

                setTimeout(() => {
                    setIsEnterLoad(false);
                }, 500)
            } else itemRefs.current[activeIndex].classList.add("is-active");
            
        }catch{}
    }, [activeIndex])

    const responsiveListener = () => {
        let curWidth = window.innerWidth;
        if(curWidth < 900)setUiTrim(true); else setUiTrim(false)

        let maxVideoTileWidth = (window.innerHeight/4) * 1.33 * 1.25;
        if (maxVideoTileWidth <= 600) setUseMobileVideo(true);
        else setUseMobileVideo(false);

    }

    useEffect(() => {
        if(isFromArchive){
            try{
                mHeroRef.current.classList.add("from-archive")
            }catch{}
        } else{
            try{
                mHeroRef.current.classList.add("not-from-archive")
            }catch{}
        }

        if(isFirstLoad){
            try{
                itemWrapperRef.current.style.overflow = "hidden";
                setTimeout(() => {
                    itemWrapperRef.current.style.overflow = null
                }, extVideoEndPoint*1000)
            }catch{}
            

            let yFLScale = (window.innerHeight / ((window.innerHeight/4) - 20))
            let xFlScale = yFLScale * 1.33 * 1.25;
            let targAR = (((window.innerHeight/4)- 20)*1.33 * 1.25)/((window.innerHeight/4)-20);
            setFirstLoadScalers([xFlScale, yFLScale, targAR]);
        }else{
            setFirstLoadScalers(false)
        }

        responsiveListener();
        window.addEventListener("resize", responsiveListener)

        try{
            let activeTarg = 0;
            let postSibling = 1

            scrollLineRefs.current[0].style.transform = "scaleX(1.75)";
            scrollLineRefs.current[0].style.background = "var(--light-color)";
            if(postSibling !== activeTarg){
                scrollLineRefs.current[postSibling].style.transform = "scaleX(1.25)";
                scrollLineRefs.current[postSibling].style.background = "var(--unselected-color)";
            }
        }catch{}


        return() => {
            setTriggerHomepageToProjectTransition(false);
        }
    }, [])

    return(
        <motion.div 
            className="ts_homepage__wrapper"
            initial="initial"
            animate="animate"
            exit="exit"
        >
            <div className="m_hero__wrapper" 
                ref={mHeroRef} 
                style={{zIndex: 1, paddingBottom: 0, pointerEvents: "none"}}>
                <div className="m-main-hero__wrapper" 
                    style={{overflow: "hidden"}}
                    ref={heroSpanRef}    
                >
                    <div className="hero_img__wrapper"
                        style={{
                            zIndex: 0,
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center"
                        }}
                    >
                        <motion.img 
                                className="home-logo-upper"
                                src="../media/branding/logo-upper.png" 
                                variants={mainHeroVariants}
                                custom={[0, isFirstLoad]}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            />
                    </div>
                    <div className="lower-logo-wrapper">
                        <motion.img
                            className="home-lower-logo"
                            src="../media/branding/logo-lower.png"
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            variants={subHeroVariants}
                            custom={[0, isFirstLoad, 0.15]}
                        />
                    </div>
                </div>
            </div>
            <div 
                className="ts-items__wrapper" 
                ref={itemWrapperRef}
            >
                {firstLoadScalers !== null && useMobileVideo !== null && projData.map((projItem, p_idx) => (
                    <motion.div 
                        className="it_wr_r"
                        ref={el => itemRefs.current[p_idx] = el}
                        style={{
                            height: "calc(25dvh - 20px)",
                            aspectRatio: "1.3333",
                            left: "calc(50% - 20px)",
                            margin: 20,
                            position: "relative",
                            transform: p_idx == 0 ? "translate3d(-50%, 0, 0) scale(1.25, 1)" : "translate3d(-50%, 0, 0) scale(1, 1)",
                            opacity: p_idx == 0 ? 1 : null,
                            pointerEvents: "all",
                            filter: !isColourMode && (activeIndex !== p_idx) ? "grayscale(1)" : "grayscale(0)",
                            marginBottom: p_idx == itemRefs.current.length - 1 ? "40dvh" : null
                        }}
                        variants={tsItemVariants}
                        onClick={() => {handleItemClick(p_idx)}}
                        exit={`${Math.abs((p_idx) - activeIndex) <= 2 ? 'exit' : "initial"}`}
                        custom={[
                            (p_idx - activeIndex),
                            p_idx == activeIndex,
                            selected,
                            p_idx <= 2,
                            window.location.href.includes("archive"),
                        ]}
                    >
                        <motion.div 
                            className="it_main_wrap"
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            variants={p_idx <= 2 ? itemFLVariants : {}}    
                            custom={[firstLoadScalers, p_idx == 0, p_idx]}
                        >
                            <motion.div className="itm_sclr__wrapper"
                                initial={ (p_idx < 4 &&  p_idx > 0) || (p_idx == 0 && !isFromArchive) ? {
                                    transform: "translate3d(0, 100%, 0)"
                                } : {}} animate={{
                                    transform: "translate3d(0, 0%, 0)",
                                    transition: {
                                        duration: 1.5,
                                        delay: p_idx * 0.1,
                                        ease: [0.16, 1, 0.3, 1]
                                    }
                                }} exit={
                                    ((p_idx+ 2 - activeIndex >= 0) && (p_idx+ 2 - activeIndex <= 4) && (p_idx !== activeIndex || !selected) && !(window.location.href.includes("archive") && p_idx == activeIndex)) 
                                    ? 
                                        {
                                            transform: "translate3d(0, 120%, 0)",
                                            transition: {
                                                delay: window.location.href.includes("archive") ? 0.025* (p_idx+2 - activeIndex) : 0.05 * (p_idx+2 - activeIndex),
                                                duration: window.location.href.includes("archive") ? 0.45 : 0.5,
                                                ease: [0.7, 0, 0.84, 0]
                                            }
                                        }
                                    : {}
                                }
                                style={{
                                    width: "100%",
                                    height: "100%"
                                }}
                            >
                                <div 
                                    className="itm_rev_sclr"
                                    ref={el => revSclrRefs.current[p_idx] = el}    
                                >
                                    <div className="ts-it-thumb__wrapper" 
                                        style={{
                                            opacity: `${activeIndex == (p_idx) && activeGoTo && !isEnterLoad ? 0 : 1}`,
                                            transition: "opacity 0.5s ease",
                                            transitionDelay: isEnterLoad ? "2s" : null
                                        }}
                                    >
                                        <TsThumbComponent 
                                            projItem={projItem}
                                            p_idx={p_idx}
                                        />
                                    </div>
                                        <motion.div 
                                            className="ts-thumb__wrapper"
                                            style={{
                                                top: 0,
                                                left: 0,
                                                opacity: `${activeIndex == (p_idx) && activeGoTo && !isEnterLoad ? 1 : 0}`,
                                                transition: "opacity 0.5s ease"
                                            }}    
                                        >
                                            <div>
                                                <video
                                                    muted
                                                    autoPlay
                                                    loop
                                                    playsInline
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover"
                                                    }}
                                                >
                                                    <source src={
                                                        useMobileVideo ?
                                                            projItem.gifStyling.mobileVideo
                                                        :
                                                            projItem.gifStyling.backgroundImage
                                                        } type="video/mp4"  media="(min-width: 601px)"/>
                                                </video>
                                            </div>
                                        </motion.div>
                                </div>
                            </motion.div>
                        </motion.div>
                        <CornerHover 
                            // isSelected={(hasSelected && p_idx == selectedIndex) || (p_idx == activeIndex && defaultExit)}
                            isSelected={(selected || defaultExit) && p_idx == activeIndex}
                            performInitial={p_idx == 0}
                            counterYScaler= {1.25}
                        />
                    </motion.div>
                ))}
                
            </div>
            {/* <NewColourMode 
                isColourMode={isColourMode}
                setIsColourMode={setIsColourMode}
                customStyling={{
                    left: "20px",
                    bottom: "unset",
                    right: "unset",
                    top: "50%",
                    transform: "translateY(50%) rotate(270deg)",
                    transformOrigin: "left"
                }}
            /> */}
            {
                firstLoadScalers !== null &&
            <motion.div
                className="scroll_line__wrapper"
                ref={scrollLineWrapperRefs}
                variants={tsScrollLineWrapperVariants}
                custom={firstLoadScalers}
                style={{
                    left: "20px"
                }}
            >
                {projData.map((item, idx) => (
                    <div onClick={() => scrollLineClick(idx)}>
                        <span 
                            className="scroll-line" 
                            ref={el => scrollLineRefs.current[idx] = el}
                            
                        />
                    </div>
                ))}
            </motion.div>
            }
            { uiTrim !== null && firstLoadScalers !== null &&
                <motion.div 
                    className="ts-meta_info__wrapper"
                    variants={testVariants}
                >
                    <div className="ts__left">
                        <div className="ts-film__wrapper" onClick={toggleFilmMode} style={{pointerEvents: "all"}}>
                            <AnimatePresence mode="sync">
                                {
                                    isFilmMode ?
                                        <motion.span
                                            key={`ts-fm-${isFilmMode}`}
                                            style={{
                                                display: "inline-block",
                                                position: "absolute",
                                                left: 0
                                            }}
                                            initial="initial"
                                            animate="animate"
                                            exit="exit"
                                            variants={fmVariants}
                                            custom={firstLoadScalers}
                                        >{uiTrim ? 'FILM' : 'FILM MODE'}</motion.span>
                                    : 
                                        <motion.span
                                            key={`ts-fm-d-${isFilmMode}`}
                                            style={{
                                                display: "inline-block",
                                                position: "absolute",
                                                left: 0
                                            }}
                                            initial="initial"
                                            animate="animate"
                                            exit="exit"
                                            variants={fmVariants}
                                            custom={firstLoadScalers}
                                        >{uiTrim ? 'DIGI' : 'DIGI MODE'}</motion.span>
                                }
                            </AnimatePresence>
                        </div>
                        <div className="ts-title__wrapper">
                            <AnimatePresence mode="sync">
                                {
                                    <motion.span
                                        key={'title-'+activeMetaIndex}
                                        className="mta-inf-item"
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        variants={metaItemVariants}
                                        custom={[0.075, firstLoadScalers]}
                                    >
                                        {projData[activeMetaIndex].title}
                                    </motion.span>
                                }
                            </AnimatePresence>
                        </div>
                    </div>
                    <div className="ts-dc" style={{minWidth: (20 + (window.innerHeight/4) * 1.33 * 1.25)+"px"}}/>
                    <div className="ts__right">
                    <div className="ts-artist__wrapper">
                        <AnimatePresence mode="sync">
                            {
                                <motion.span
                                    key={'artist-'+activeMetaIndex}
                                    className="mta-inf-item"
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    variants={metaItemVariants}
                                    custom={[0.15, firstLoadScalers]}
                                >
                                    {projData[activeMetaIndex].artist}
                                </motion.span>
                            }
                        </AnimatePresence>
                    </div>
                    <div className="ts-proj-num__wrapper" >
                        <AnimatePresence mode="sync">
                            {uiTrim && <span style={{
                                opacity: 0
                            }}>99</span>}
                            <motion.span
                                key={'proj-num-'+activeMetaIndex}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                variants={metaItemVariants}
                                custom={[0.225, firstLoadScalers]}
                                style={{
                                    display: "inline-block",
                                    position: "absolute",
                                    left: !uiTrim ? 0 : null,
                                    right: uiTrim ? 0 : null
                                }}
                            >
                                {activeMetaIndex < 9 ? '0' + activeMetaIndex : activeMetaIndex+1}
                            </motion.span>
                            </AnimatePresence>
                            {!uiTrim &&
                                <motion.span style={{ display: "inline-block"}}
                                    initial="initial"
                                    animate="animate"
                                    exit="exit"
                                    variants={metaItemVariants}
                                    custom={[0.25, firstLoadScalers]}
                                >{projData.length}</motion.span>
                            }
                    </div>
                    </div>
                    
                    
                </motion.div>
            }
             <div 
                className="fl-home-wrapper"
            >
                    {
                        firstLoadScalers &&
                        <motion.div
                            className="fl-video"
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                height: "100dvh",
                                width: `calc(100dvh * ${firstLoadScalers[2]})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                zIndex: 99,
                                backgroundImage: 'url('.concat(projData[0].thumbnails.laptop,')'),
                                trasnformOrigin: "center"
                            }}
                            initial={{
                                opacity: 1,
                                transform: "translate3d(-50%, -50%, 0) scale3d(1, 1, 1)"
                            }}
                            animate={{
                                opacity: 0,
                                transform: `translate3d(-50%, -50%, 0) scale3d(${1/firstLoadScalers[1]}, ${1/firstLoadScalers[1]}, 1)`,
                                transition: {
                                    delay: extVideoInStart,
                                    duration: videoShrinkDuration,
                                    ease: [0.87, 0, 0.13, 1],
                                    opacity: {
                                        duration: 0.25,
                                        delay: extVideoEndPoint
                                    }
                                }
                            }}
                        />
                    }
                </div>
            <motion.div 
                key={"noise_overlay"}
                className="noise_overlay" 
                initial={{
                    opacity: 0
                }}
                animate={ isFilmMode ? {
                    opacity: 1,
                    transition: {
                        duration: 1,
                        ease: "easeOut"
                    }
                } : {
                    opacity: 0,
                    transition: {
                        duration: 1,
                        ease: "easeOut"
                    }
                }} exit={{
                    opacity: 0,
                    transition: {
                        duration: 0.5,
                        ease: "easeOut"
                    }
                }}
            />
        </motion.div>
    )
}

export default HomepageTs;